/**
 * BulkDialog.jsx (iter142 refactor — extracted from CustomerPricing.jsx)
 *
 * Bulk-apply price rule dialog. Owns its own form state (tier / category /
 * mode / value) and only surfaces the API call + result to the parent.
 *
 * Parent supplies:
 *   open, onClose, tiers, categories                — config
 *   onRun(payload) → Promise<resultDoc>             — POSTs /bulk-override
 *   onApplied()                                     — notify parent to refresh matrix
 */
import React, { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../ui/dialog';
import { Lightning, Trash } from '@phosphor-icons/react';

export const BulkDialog = ({ open, onClose, tiers, categories, onRun, onApplied }) => {
  const [tier, setTier] = useState('tier1');
  const [category, setCategory] = useState('');
  const [mode, setMode] = useState('discount'); // discount|markup|clear
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const handleClose = () => {
    if (busy) return;
    setResult(null);
    onClose();
  };

  const run = async () => {
    setBusy(true);
    setResult(null);
    try {
      const payload = { tier_key: tier };
      if (category) payload.category = category;
      if (mode === 'clear') {
        payload.clear = true;
      } else {
        const v = parseFloat(value);
        if (Number.isNaN(v) || v < -100 || v > 1000) {
          toast.error('Enter a number between -100 and 1000.');
          setBusy(false);
          return;
        }
        if (mode === 'discount') payload.discount_pct = v;
        else payload.markup_pct = v;
      }
      const data = await onRun(payload);
      setResult(data);
      const msg = data.mode === 'clear'
        ? `${data.overrides_deleted} overrides cleared`
        : `${data.applied_count} of ${data.scope_sku_count} SKUs updated` +
          (data.skipped_margin_floor?.length ? ` · ${data.skipped_margin_floor.length} skipped (margin floor)` : '');
      toast.success(msg);
      onApplied?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bulk operation failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); }}>
      <DialogContent className="border border-border rounded-none max-w-2xl" data-testid="bulk-dialog">
        <DialogHeader>
          <DialogTitle className="font-mono flex items-center gap-2">
            <Lightning size={18} weight="fill" className="text-amber-600" /> Bulk apply price rule
          </DialogTitle>
          <DialogDescription className="text-xs">
            Apply one discount or markup % across every SKU (optionally filtered by category) at the chosen tier.
            Margin-floor breaches are skipped (block mode) or flagged (warn mode).
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="overline" htmlFor="bulk-tier">Tier</Label>
              <select
                id="bulk-tier"
                value={tier}
                onChange={(e) => setTier(e.target.value)}
                className="mt-1 w-full text-sm px-3 py-2 border border-border bg-background"
                data-testid="bulk-tier-select"
              >
                {(tiers || []).map((t) => (
                  <option key={t.key} value={t.key}>{t.name} ({t.key})</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="overline" htmlFor="bulk-category">Category (optional)</Label>
              <select
                id="bulk-category"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="mt-1 w-full text-sm px-3 py-2 border border-border bg-background"
                data-testid="bulk-category-select"
              >
                <option value="">All categories</option>
                {categories.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-[1fr_1fr] gap-3">
            <div>
              <Label className="overline">Mode</Label>
              <div className="mt-1 inline-flex border border-border w-full">
                {['discount', 'markup', 'clear'].map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => setMode(m)}
                    className={`flex-1 text-xs px-3 py-2 uppercase font-bold tracking-widest border-r border-border last:border-r-0 ${mode === m ? 'bg-brand text-white' : 'bg-background text-muted-foreground hover:bg-muted/50'}`}
                    data-testid={`bulk-mode-${m}`}
                  >
                    {m === 'clear' ? <><Trash size={11} className="inline mr-1" /> Clear</> : m}
                  </button>
                ))}
              </div>
            </div>
            {mode !== 'clear' && (
              <div>
                <Label className="overline" htmlFor="bulk-value">
                  {mode === 'discount' ? 'Discount % off list' : 'Markup % over cost'}
                </Label>
                <Input
                  id="bulk-value"
                  type="number"
                  step="0.5"
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  placeholder={mode === 'discount' ? 'e.g. 25' : 'e.g. 40'}
                  className="mt-1 tabular-nums font-mono"
                  data-testid="bulk-value-input"
                />
              </div>
            )}
          </div>

          <div className="text-[11px] text-muted-foreground italic leading-snug">
            {mode === 'clear'
              ? `Will DELETE every override at tier ${tier}${category ? ` for category “${category}”` : ' across all SKUs'}.`
              : mode === 'discount'
                ? `new_price = list_price × (1 − ${value || 0}%)`
                : `new_price = landed_cost × (1 + ${value || 0}%)`}
          </div>

          {result && (
            <div className="border border-border bg-slate-50 px-3 py-2 text-xs space-y-1" data-testid="bulk-result">
              {result.mode === 'clear' ? (
                <div className="flex justify-between"><span>Overrides deleted</span><span className="font-mono font-bold">{result.overrides_deleted}</span></div>
              ) : (
                <>
                  <div className="flex justify-between"><span>SKUs in scope</span><span className="font-mono font-bold">{result.scope_sku_count}</span></div>
                  <div className="flex justify-between"><span>Overrides applied</span><span className="font-mono font-bold text-emerald-700">{result.applied_count}</span></div>
                  {result.skipped_no_basis?.length > 0 && (
                    <div className="flex justify-between"><span>Skipped (no cost / no list)</span><span className="font-mono text-muted-foreground">{result.skipped_no_basis.length}</span></div>
                  )}
                  {result.skipped_margin_floor?.length > 0 && (
                    <div className="flex justify-between text-rose-700"><span>Skipped (margin floor)</span><span className="font-mono font-bold">{result.skipped_margin_floor.length}</span></div>
                  )}
                  {result.warnings?.length > 0 && (
                    <div className="flex justify-between text-amber-700"><span>Applied with warning</span><span className="font-mono font-bold">{result.warnings.length}</span></div>
                  )}
                </>
              )}
              {result.skipped_margin_floor?.length > 0 && (
                <details className="mt-2">
                  <summary className="cursor-pointer text-rose-700 font-bold text-xs">View skipped SKUs ({result.skipped_margin_floor.length})</summary>
                  <ul className="mt-1 max-h-32 overflow-y-auto font-mono text-[10px] space-y-0.5 pl-3">
                    {result.skipped_margin_floor.slice(0, 100).map((s, i) => (
                      <li key={i} data-testid={`bulk-skipped-${s.sku_code}`}>
                        {s.sku_code} → ${s.proposed_price?.toFixed(2)} ({s.margin_pct}%)
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={busy}
              className="rounded-none"
              data-testid="bulk-cancel-btn"
            >Close</Button>
            <Button
              onClick={run}
              disabled={busy || (mode !== 'clear' && value === '')}
              className={`rounded-none text-white ${mode === 'clear' ? 'bg-rose-600 hover:bg-rose-700' : 'bg-brand hover:bg-brand/90'}`}
              data-testid="bulk-run-btn"
            >
              {busy ? 'Running…' : mode === 'clear' ? 'Clear overrides' : 'Apply to all SKUs'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
